import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Drawer,
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Typography,
  Divider,
  IconButton,
  useTheme,
  useMediaQuery,
  Tooltip,
  Avatar,
  Chip
} from '@mui/material';
import {
  IconChevronDown,
  IconChevronRight,
  IconMenu2,
  IconX,
  IconLogout
} from '@tabler/icons-react';
import { useAuth } from '../../shared/hooks/useAuth';
import { getMenuByRoleId, ROLE_NAMES } from '../../menu-items/menuSelector';
import { getAPI } from '../../shared/services/axios/api';
import { resolveAssetUrl, AVATAR_UPDATED_EVENT } from '../../shared/utils/assets';

interface SidebarProps {
  open: boolean;
  onClose?: () => void;
  variant?: 'permanent' | 'persistent' | 'temporary';
}

interface MenuItem {
  id: string;
  title: string;
  type: 'group' | 'item' | 'collapse';
  url?: string;
  icon?: any;
  breadcrumbs?: boolean;
  children?: MenuItem[];
}

const DRAWER_WIDTH = 260;
const DRAWER_WIDTH_COLLAPSED = 80;

const Sidebar: React.FC<SidebarProps> = ({ open, onClose, variant = 'persistent' }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user, userRole, logout } = useAuth();

  const [collapsed, setCollapsed] = useState(false);
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  // The stored session user has no image field, so the avatar comes from the
  // profile endpoint. Kept in sync via the event the uploader fires, so
  // changing it in Settings updates here without a reload.
  const [avatar, setAvatar] = useState('');

  useEffect(() => {
    let cancelled = false;

    if (user) {
      getAPI('auth/profile')
        .then((response: any) => {
          if (!cancelled && response?.success && response.data?.avatar) {
            setAvatar(response.data.avatar);
          }
        })
        .catch(() => undefined); // a missing avatar must never break the nav
    }

    const onAvatarChange = (event: Event) => {
      setAvatar(String((event as CustomEvent).detail || ''));
    };
    window.addEventListener(AVATAR_UPDATED_EVENT, onAvatarChange);

    return () => {
      cancelled = true;
      window.removeEventListener(AVATAR_UPDATED_EVENT, onAvatarChange);
    };
  }, [user?.id]);

  // Get menu items based on user role
  const menuItems = getMenuByRoleId(user?.role_id);

  const handleToggleCollapse = () => {
    setCollapsed(!collapsed);
    setExpandedItems([]); // Collapse all items when toggling sidebar
  };

  const handleExpandItem = (itemId: string) => {
    setExpandedItems((prev) =>
      prev.includes(itemId) ? prev.filter((id) => id !== itemId) : [...prev, itemId]
    );
  };

  const handleNavigation = (url: string) => {
    navigate(url);
    if (isMobile && onClose) {
      onClose();
    }
  };

  const handleLogout = () => {
    logout();
    // Force navigation after logout
    setTimeout(() => {
      navigate('/login', { replace: true });
    }, 0);
  };

  const isActiveRoute = (url: string): boolean => {
    return location.pathname === url || location.pathname.startsWith(`${url}/`);
  };

  const getRoleName = (roleId: number | undefined): string => {
    if (!roleId) return 'Guest';
    return ROLE_NAMES[roleId as keyof typeof ROLE_NAMES] || 'Unknown';
  };

  const getRoleColor = (roleId: number | undefined): string => {
    switch (roleId) {
      case 1:
        return '#5e35b1'; // Owner - Purple
      case 2:
        return '#1976d2'; // Dealer - Blue
      case 3:
        return '#2e7d32'; // Reseller - Green
      case 4:
        return '#ed6c02'; // Customer - Orange
      default:
        return '#757575'; // Default - Gray
    }
  };

  const renderMenuItem = (item: MenuItem, level: number = 0) => {
    const isExpanded = expandedItems.includes(item.id);
    const isActive = item.url ? isActiveRoute(item.url) : false;
    const Icon = item.icon;

    if (item.type === 'group') {
      return (
        <React.Fragment key={item.id}>
          {!collapsed && item.title && (
            <Typography
              variant="caption"
              sx={{
                px: 3,
                pt: 2,
                pb: 1,
                color: 'text.secondary',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}
            >
              {item.title}
            </Typography>
          )}
          {item.children?.map((child) => renderMenuItem(child, level))}
        </React.Fragment>
      );
    }

    if (item.type === 'collapse') {
      return (
        <React.Fragment key={item.id}>
          <ListItem disablePadding sx={{ display: 'block' }}>
            <Tooltip title={collapsed ? item.title : ''} placement="right">
              <ListItemButton
                onClick={() => !collapsed && handleExpandItem(item.id)}
                sx={{
                  minHeight: 48,
                  justifyContent: open ? 'initial' : 'center',
                  px: 2.5,
                  pl: level * 2 + 2.5,
                  borderRadius: '8px',
                  mx: 1,
                  mb: 0.5,
                  color: 'text.primary',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: '#1976d2',
                    color: '#ffffff',
                    '& .MuiListItemIcon-root': {
                      color: '#ffffff'
                    }
                  }
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: collapsed ? 'auto' : 2,
                    justifyContent: 'center',
                    color: '#5e35b1',
                    transition: 'color 0.2s ease'
                  }}
                >
                  {Icon && <Icon size={20} />}
                </ListItemIcon>
                {!collapsed && (
                  <>
                    <ListItemText
                      primary={item.title}
                      primaryTypographyProps={{
                        fontSize: '0.875rem',
                        fontWeight: 500
                      }}
                    />
                    {isExpanded ? <IconChevronDown size={18} /> : <IconChevronRight size={18} />}
                  </>
                )}
              </ListItemButton>
            </Tooltip>
          </ListItem>
          {!collapsed && (
            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {item.children?.map((child) => renderMenuItem(child, level + 1))}
              </List>
            </Collapse>
          )}
        </React.Fragment>
      );
    }

    if (item.type === 'item' && item.url) {
      return (
        <ListItem key={item.id} disablePadding sx={{ display: 'block' }}>
          <Tooltip title={collapsed ? item.title : ''} placement="right">
            <ListItemButton
              onClick={() => handleNavigation(item.url!)}
              selected={isActive}
              sx={{
                minHeight: 48,
                justifyContent: open ? 'initial' : 'center',
                px: 2.5,
                pl: level * 2 + 2.5,
                borderRadius: '8px',
                mx: 1,
                mb: 0.5,
                backgroundColor: isActive ? '#1976d2' : 'transparent',
                color: isActive ? '#ffffff' : 'text.primary',
                fontWeight: isActive ? 600 : 500,
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: '#1976d2',
                  color: '#ffffff',
                  '& .MuiListItemIcon-root': {
                    color: '#ffffff'
                  }
                },
                '&.Mui-selected': {
                  backgroundColor: '#1976d2',
                  color: '#ffffff',
                  '&:hover': {
                    backgroundColor: '#1976d2'
                  }
                }
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: collapsed ? 'auto' : 2,
                  justifyContent: 'center',
                  color: isActive ? '#ffffff' : '#5e35b1',
                  transition: 'color 0.2s ease'
                }}
              >
                {Icon && <Icon size={20} />}
              </ListItemIcon>
              {!collapsed && (
                <ListItemText
                  primary={item.title}
                  primaryTypographyProps={{
                    fontSize: '0.875rem',
                    fontWeight: isActive ? 600 : 500
                  }}
                />
              )}
            </ListItemButton>
          </Tooltip>
        </ListItem>
      );
    }

    return null;
  };

  const drawerContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header */}
      <Box
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: `1px solid ${theme.palette.divider}`
        }}
      >
        {!collapsed && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6" fontWeight={700} color="primary">
              Partner Portal
            </Typography>
          </Box>
        )}
        {!isMobile && (
          <IconButton onClick={handleToggleCollapse} size="small">
            {collapsed ? <IconMenu2 size={20} /> : <IconX size={20} />}
          </IconButton>
        )}
      </Box>

      {/* User Info */}
      {!collapsed && user && (
        <Box
          sx={{
            p: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            borderBottom: `1px solid ${theme.palette.divider}`
          }}
        >
          <Avatar
            src={avatar ? resolveAssetUrl(avatar) : undefined}
            alt={user.name || 'User'}
            sx={{
              width: 40,
              height: 40,
              bgcolor: getRoleColor(user.role_id),
              fontSize: '1rem',
              fontWeight: 600
            }}
          >
            {user.name?.charAt(0).toUpperCase() || 'U'}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle2" fontWeight={600} noWrap>
              {user.name || 'User'}
            </Typography>
            <Chip
              label={getRoleName(user.role_id).toUpperCase()}
              size="small"
              sx={{
                height: 20,
                fontSize: '0.65rem',
                fontWeight: 600,
                bgcolor: getRoleColor(user.role_id),
                color: 'white',
                mt: 0.5
              }}
            />
          </Box>
        </Box>
      )}

      {/* Menu Items */}
      <Box sx={{ flexGrow: 1, overflowY: 'auto', py: 1 }}>
        <List>
          {menuItems.items.map((item) => renderMenuItem(item))}
        </List>
      </Box>

      {/* Footer - Logout */}
      <Divider />
      <Box sx={{ p: 1 }}>
        <Tooltip title={collapsed ? 'Logout' : ''} placement="right">
          <ListItemButton
            onClick={handleLogout}
            sx={{
              minHeight: 48,
              justifyContent: collapsed ? 'center' : 'initial',
              px: 2.5,
              borderRadius: '8px',
              color: 'error.main',
              '&:hover': {
                backgroundColor: 'error.light',
                color: 'error.dark'
              }
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: 0,
                mr: collapsed ? 'auto' : 2,
                justifyContent: 'center',
                color: 'inherit'
              }}
            >
              <IconLogout size={20} />
            </ListItemIcon>
            {!collapsed && (
              <ListItemText
                primary="Logout"
                primaryTypographyProps={{
                  fontSize: '0.875rem',
                  fontWeight: 600
                }}
              />
            )}
          </ListItemButton>
        </Tooltip>
      </Box>
    </Box>
  );

  return (
    <Drawer
      variant={isMobile ? 'temporary' : variant}
      open={isMobile ? open : true}
      onClose={onClose}
      sx={{
        width: collapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: collapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH,
          boxSizing: 'border-box',
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen
          }),
          overflowX: 'hidden',
          borderRight: `1px solid ${theme.palette.divider}`
        }
      }}
    >
      {drawerContent}
    </Drawer>
  );
};

export default Sidebar;

